import React, { useState } from "react";
import {
  ComponentDetailsByFileOut,
  LicenseFeatureEnum,
  InternalNoEPDSProductSearchOutput,
  ProductType,
} from "api-client";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import { LinkIcon } from "@heroicons/react/outline";
import Collapse from "components/Collapse";
import clsx from "clsx";
import { searchStore } from "features/ProductSearch/SearchStore";
import { useParams } from "react-router";
import ProductDetailsModal from "features/ProductDetailsModal";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import DatasetsView, { EPDGroup } from "../components/DatasetsView";
import ProductInfo from "../components/ProductInfo";
import { createEpdsList } from "features/MappingEditor/utils";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { IconCancel, IconWorld } from "@tabler/icons-react";
import { observer } from "mobx-react-lite";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { USER_ROLES } from "utils";
import ConfirmModal from "components/ConfirmModal";
import useProductActions from "../useProductActions";

interface ProductRowProps {
  product: InternalNoEPDSProductSearchOutput;
  match?: (product_id: string) => Promise<void>;
  testID?: string;
  settings?: React.ReactNode;
  mappingProgress?: boolean;
  onMappingProgressChange?: (isInProgress: boolean) => void;
  className?: string;
}

function ProductRow({
  product,
  match,
  testID,
  settings,
  mappingProgress = false,
  onMappingProgressChange,
  className,
}: ProductRowProps) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { ifc_id } = useParams();
  const [openDetailsPopup, setOpenDetailsPopup] = React.useState(false);
  const [epdGroups, setEpdGroups] = React.useState<EPDGroup[] | undefined>();
  const [loadingDataset, setLoadingDataset] = React.useState(false);
  const { hasFeature } = useFeatureAccess();
  const {
    openPublishConfirmModal,
    handleChangeProductStatus,
    setOpenPublishConfirmModal,
  } = useProductActions();

  const handleMapEpd = React.useCallback(async () => {
    if (product?.id) {
      setLoading(true);
      onMappingProgressChange?.(true);
      await match?.(product.id);
      setLoading(false);
      onMappingProgressChange?.(false);
    }
  }, []);

  const handleAdd = () => {
    COEStore.addChildItem(product as ComponentDetailsByFileOut);
    COEStore.setOpenedSearchInCE(false);
  };

  async function getProductDetails(productId: string) {
    if (epdGroups?.length) return;
    setLoadingDataset(true);
    const details = await searchStore.getProductDetails(productId, ifc_id);
    if (details) {
      const groups = details?.epd_groups?.map((group) => {
        const epdsList = createEpdsList(group);
        return {
          epds: epdsList,
          conformity: group.conformity,
          certifications: group.certifications,
        };
      });
      setEpdGroups(groups);
    }
    setLoadingDataset(false);
  }

  function showDataset() {
    if (loadingDataset) return skeleton();
    return (
      <div className="px-4 pb-4">
        {epdGroups?.length
          ? epdGroups.map((item, index) => (
              <DatasetsView
                key={index}
                epdGroup={item}
                referenceUnitID={product.reference_unit}
              />
            ))
          : null}
      </div>
    );
  }

  function hoverActions() {
    const isUserAdmin =
      sidebarMenuStore.currentOrganisation?.role === USER_ROLES[0].id;

    const shouldShowPublishButton =
      hasFeature(LicenseFeatureEnum.ProductPublishing) &&
      searchStore.usingOrganisationSearch &&
      isUserAdmin &&
      !COEStore.openedSearchInCE;

    return (
      <div className="pr-3 bg-indigo-50 whitespace-nowrap flex justify-center items-center">
        {settings || null}
        {COEStore.openedSearchInCE && (
          <Button
            className="cursor-pointer ml-3 !h-[35px]"
            width="fit-content"
            type="primary"
            onClick={handleAdd}
            leadingIcon={<LinkIcon />}
            testID={"addItem_Button"}
          >
            {t("mapping.addProduct", {
              name:
                searchStore.selectedTab?.type === "PRODUCT"
                  ? t("mapping.product")
                  : t("mapping.component"),
            })}
          </Button>
        )}

        {!COEStore.openedSearchInCE && !searchStore.usingOrganisationSearch && (
          <Button
            className={clsx(
              "ml-1 !h-[35px]",
              mappingProgress ? "cursor-not-allowed" : "cursor-pointer",
              match ? "" : "hidden"
            )}
            width="fit-content"
            type="primary"
            onClick={handleMapEpd}
            leadingIcon={<LinkIcon />}
            loading={loading}
            disable={mappingProgress}
            testID={testID}
          >
            {t("mapping.linkProduct", {
              name:
                searchStore.selectedTab?.type === "PRODUCT"
                  ? t("mapping.product")
                  : t("mapping.component"),
            })}
          </Button>
        )}

        {shouldShowPublishButton && (
          <Button
            className="ml-1 !h-[35px]"
            leadingIcon={product.is_public ? <IconCancel /> : <IconWorld />}
            onClick={() =>
              handleChangeProductStatus(
                product.is_public,
                product.name,
                product.id
              )
            }
          >
            {product.is_public
              ? t("productSearch.unpublish")
              : t("productSearch.publish")}
          </Button>
        )}
      </div>
    );
  }

  async function onOpenCollapse(open: boolean) {
    if (!open || !product.id) return;
    await getProductDetails(product.id);
  }

  return (
    <div
      className={clsx("overflow-hidden border-b border-gray-200", className)}
    >
      <div
        className={clsx(
          "text-gray-500 border-b border-gray-100 last-of-type:border-b-0 md:flex",
          "items-center justify-between relative group/productRow transition"
        )}
      >
        <ProductInfo
          item={product}
          containerClassName="group-hover/productRow:to-indigo-50"
        />
        <div
          className={clsx(
            "hidden absolute right-0 top-0 group-hover/productRow:flex h-full transition"
          )}
        >
          <div className="w-10 bg-gradient-to-r from-transparent to-bg-indigo-50"></div>
          {hoverActions()}
        </div>
      </div>

      {product.type === ProductType.Product && (
        <Collapse
          headerTitle={t("mapping.hideDataset")}
          closeTitle={t("mapping.showDataset")}
          defaultClose
          headerClassName={clsx(
            "!bg-white text-indigo-600 hover:text-indigo-700 text-sm font-medium px-0 border-b-0 rounded-b-md"
          )}
          onOpen={onOpenCollapse}
        >
          {showDataset()}
        </Collapse>
      )}

      <ProductDetailsModal
        productId={product.id}
        productName={product.name}
        productType={product.type}
        open={openDetailsPopup}
        close={() => setOpenDetailsPopup(false)}
        isPublic={product.is_public}
      />

      <ConfirmModal
        type="warning"
        open={openPublishConfirmModal}
        close={() => setOpenPublishConfirmModal(false)}
        title={t("productSearch.unpublishConfirmTitle", {
          product_name: product.name,
        })}
        descriptionMesage={t("productSearch.unpublishConfirmDesc")}
        confirmButtonTitle={
          <div className="flex items-center">
            <IconCancel className="mr-2" />
            {t("productSearch.unpublishConfirmButton")}
          </div>
        }
        onConfirm={() =>
          handleChangeProductStatus(product.is_public, product.name, product.id)
        }
      />
    </div>
  );
}

export default observer(ProductRow);

function skeleton() {
  return (
    <div className="animate-pulse px-4">
      <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-600/50 mb-2"></div>
      <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-600/50 mb-2"></div>
      <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-600/50 mb-2"></div>
    </div>
  );
}
